import { SettingEnabledLozenge } from 'components/node_settings/SettingEnabledLozenge';
import { BorderlessSettingsSection } from 'components/node_settings/styled';
import { useUser } from 'contexts/UserContext';
import { Setting } from 'pages/Settings/V4SensorSettings/Setting';
import {
    SectionDescription,
    SectionHeading,
    SectionTitle,
    SettingsList,
} from 'pages/Settings/V4SensorSettings/styled';
import { V4SensorSettings } from 'custom_hooks/data_hooks/v4_sensor_settings/useV4SensorSettings';
import { ReactNode } from 'react';
import { InfoPopup } from 'shared/info_popup/InfoPopup';
import styled from 'styled-components';
import { isUserInternal } from 'utils/RouteHelper';

const LabelWithInfo = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
`;

function labelWithOptionalPopup(
    label: string,
    description: string,
    useInfoPopup: boolean,
): ReactNode {
    if (!useInfoPopup) {
        return label;
    }
    return (
        <LabelWithInfo>
            {label}
            <InfoPopup title={label}>{description}</InfoPopup>
        </LabelWithInfo>
    );
}

type Props = Readonly<{
    settings: V4SensorSettings;
}>;

export function AdvancedSettingsSection({ settings }: Props) {
    const { user } = useUser();
    const useInfoPopup = isUserInternal(user?.ApplicationRole);
    const { envelopedData } = settings;

    return (
        <BorderlessSettingsSection>
            <SectionHeading>
                <SectionTitle>Advanced Settings</SectionTitle>
                <SectionDescription>
                    These settings allow you to collect vibration data for X, Y and Z axes
                    or enveloped data for the Y-axis. Battery consumption increases as
                    more settings are enabled.
                </SectionDescription>
            </SectionHeading>
            <SettingsList>
                <Setting name="Vibration Data (X Axis)">
                    <SettingEnabledLozenge enabled={settings.vibrationX} />
                </Setting>
                <Setting name="Vibration Data (Y Axis)">
                    <SettingEnabledLozenge enabled={settings.vibrationY} />
                </Setting>
                <Setting name="Vibration Data (Z Axis)">
                    <SettingEnabledLozenge enabled={settings.vibrationZ} />
                </Setting>
                <Setting
                    name={labelWithOptionalPopup(
                        'Enveloped Data (Y Axis)',
                        'Several peaks will be treated as a single peak for enveloping Y-axis data collection.',
                        useInfoPopup,
                    )}
                    helperText={
                        !useInfoPopup && envelopedData.enabled
                            ? 'Several peaks will be treated as a single peak for enveloping Y-axis data collection.'
                            : undefined
                    }
                >
                    <SettingEnabledLozenge enabled={envelopedData.enabled} />
                </Setting>
                {envelopedData.enabled && (
                    <>
                        <Setting name="Sampling Frequency" indented>
                            {envelopedData.samplingFrequencyHz} Hz
                        </Setting>
                        <Setting name="Number of Samples" indented>
                            {envelopedData.numberOfSamples} Samples
                        </Setting>
                    </>
                )}
                <Setting
                    name={labelWithOptionalPopup(
                        'Ultrasound',
                        'Acoustic frequency data will be recorded for early stage bearing fault detection.',
                        useInfoPopup,
                    )}
                    helperText={
                        !useInfoPopup
                            ? 'Acoustic frequency data will be recorded for early stage bearing fault detection.'
                            : undefined
                    }
                >
                    <SettingEnabledLozenge enabled={settings.ultrasound} />
                </Setting>
                <Setting
                    name={labelWithOptionalPopup(
                        'Turning Speed',
                        'Rotational Speed (RPM) will be recorded to support vibration analysis.',
                        useInfoPopup,
                    )}
                    helperText={
                        !useInfoPopup
                            ? 'Rotational Speed (RPM) will be recorded to support vibration analysis.'
                            : undefined
                    }
                >
                    <SettingEnabledLozenge enabled={settings.turningSpeed} />
                </Setting>
            </SettingsList>
        </BorderlessSettingsSection>
    );
}
